import React, { useRef, useEffect, useState } from 'react';
import { useGlass } from './GlassProvider';
import { scanSources, drawCombined, drawCaustic, aeroAccent } from './glassEngine';
import GlassButtonV3, { V3_FILLS } from './GlassButtonV3';

// Fills that emit the accent colour into the glass engine ([data-color]).
// The concrete hex is resolved from --aero-accent at render time.
const ACCENT_EMITTING_FILLS = new Set([
  'aero',
  'aero-lt',
  'd-aero',
  'd-aero-lt',
  'adaptive',
  'adaptive-lt',
]);

/**
 * GlassButton — a button with real-time glass reflection from nearby [data-color] elements.
 *
 * v3 (the final glass, decided 25 Oct 2026): fill="ink" (solid ink, the app's
 * primary), "ink-glass" (translucent ink that refracts what is behind it, for
 * buttons on a wash) and "glass" (white glass, the secondary) render through
 * GlassButtonV3 and the ported website engine. They also take `loading`.
 * Every other fill keeps the original engine below, unchanged, until its
 * screen moves to v3.
 *
 * Props:
 *   fill     — 'ink' | 'ink-glass' | 'glass' (v3) or the original
 *              'aero' | 'aero-lt' | 'grey' | 'white' | 'clear' | 'd-aero' | 'd-aero-lt' | 'd-glass'
 *   mode     — 'combined' (default) | 'caustic'
 *   str      — reflection strength 0-1 (default 0.25)
 *   size     — 'xs' | 'sm' | 'md' (default) | 'lg'
 *   thick    — caustic line thickness (default 2, only for caustic mode)
 *   className, style, onClick, disabled, children — pass through
 */
export default function GlassButton(props) {
  if (V3_FILLS.has(props.fill)) return <GlassButtonV3 {...props} />;
  return <GlassButtonLegacy {...props} />;
}

function GlassButtonLegacy({
  fill = 'grey',
  mode = 'combined',
  str = 0.25,
  size = 'md',
  thick = 2,
  className = '',
  children,
  ...rest
}) {
  const canvasRef = useRef(null);
  const buttonRef = useRef(null);
  const { sources, scanId, scanRadius } = useGlass();
  const [rimShadow, setRimShadow] = useState(null);

  useEffect(() => {
    const canvas = canvasRef.current;
    const button = buttonRef.current;
    if (!canvas || !button) return;

    const ctx = canvas.getContext('2d');
    const rect = button.getBoundingClientRect();
    const dpr = window.devicePixelRatio || 1;
    canvas.width = rect.width * dpr;
    canvas.height = rect.height * dpr;
    ctx.scale(dpr, dpr);

    const cx = rect.left + rect.width / 2;
    const cy = rect.top + rect.height / 2;
    const nearbySources = scanSources(cx, cy, sources, scanRadius);

    if (mode === 'caustic') {
      drawCaustic(ctx, rect.width, rect.height, nearbySources, str, thick);
    } else {
      drawCombined(ctx, rect.width, rect.height, nearbySources, str, setRimShadow);
    }
  }, [sources, scanId, mode, str, thick, scanRadius]);

  const sizeClass = size !== 'md' ? `aero-btn-${size}` : '';

  return (
    <button
      ref={buttonRef}
      className={`aero-btn aero-btn-${fill} ${sizeClass} ${className}`.trim()}
      style={rimShadow ? { boxShadow: rimShadow, ...rest.style } : rest.style}
      data-color={ACCENT_EMITTING_FILLS.has(fill) ? aeroAccent() : undefined}
      {...rest}
    >
      <canvas ref={canvasRef} className="aero-btn-canvas" />
      <span className="aero-btn-label">{children}</span>
    </button>
  );
}
